import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { FC, useCallback, useEffect, useMemo, useState } from "react";

import { Uuid } from "@/domains/global/identifiers";
import type { AudioRecordingObservable } from "@/store/audio-recordings";
import type { LocalAudioRecordingBlobRecord } from "@/domains/dexie-db/models/audio-recording";
import { useAppStore } from "@/store";
import {
  AudioRecordingProcessingStatus,
  AudioRecordingRecordingStatus,
  AudioRecordingUploadStatus,
} from "@/store/audio-recordings";
import {
  AudioRecordingSyncStatusCard,
  CapturedAudioBlobsCard,
  CapturedAudioUtilitiesCard,
  UploadProcessingCard,
} from "@/pages/audio-recordings/components";
import { computeAudioRecordingBlobStats } from "@/pages/audio-recordings/shared";
import { MDSButton, MDSButtonVariant, MDSText, MDSTextColor, MDSTextVariant } from "@/mds";

import { InfoHeader } from "./sections/info-header";
import { InfoTranscriptionSection } from "./sections/info-transcription-section";
import { InfoStatusMessage } from "./sections/info-status-message";

type Props = {
  recordingId: Uuid;
  recording?: AudioRecordingObservable;
};

const TRASH_CONFIRM_MESSAGE = "Trash local audio and transcript data for this recording?";

export const AudioRecordingDetailInfo: FC<Props> = observer(({ recordingId, recording }) => {
  const { store } = useAppStore();
  const audioStore = store.audio;
  const { localAudioRecordingBlobs: blobsRepository } = store.dexie.repositories;

  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [blobs, setBlobs] = useState<LocalAudioRecordingBlobRecord[]>([]);
  const loadRecordingData = useCallback(
    async ({
      signal,
      showSpinner = true,
    }: { signal?: { cancelled: boolean }; showSpinner?: boolean } = {}) => {
      if (showSpinner) {
        setIsLoading(true);
      } else {
        setIsRefreshing(true);
      }
      setLoadError(null);

      try {
        await store.audioRecordings.getAsync(recordingId);

        const blobRecords = await blobsRepository.getAllByRecordingId({
          associatedAudioRecordingId: recordingId,
        });

        if (signal?.cancelled) return;

        setBlobs(blobRecords);
      } catch (error) {
        if (signal?.cancelled) return;
        const message = error instanceof Error ? error.message : "Failed to load local data.";
        setLoadError(message);
        setBlobs([]);
      } finally {
        if (!signal?.cancelled && showSpinner) {
          setIsLoading(false);
        }
        if (!signal?.cancelled && !showSpinner) {
          setIsRefreshing(false);
        }
      }
    },
    [blobsRepository, recordingId, store.audioRecordings]
  );

  useEffect(() => {
    if (!store.isMemDbReady) return;
    const signal = { cancelled: false };
    void loadRecordingData({ signal });
    return () => {
      signal.cancelled = true;
    };
  }, [loadRecordingData, store.isMemDbReady]);

  const headerStats = useMemo(() => {
    const stats = computeAudioRecordingBlobStats(blobs);
    return {
      totalDurationLabel: stats.totalDurationLabel,
      totalSizeLabel: stats.totalSizeLabel,
      uploadedCount: stats.uploadedCount,
      blobCount: blobs.length,
    };
  }, [blobs]);

  const recordingObservable = recording ?? store.audioRecordings.get(recordingId);
  const recordingStatus = (recordingObservable?.recordingStatus ??
    AudioRecordingRecordingStatus.INITIAL) as AudioRecordingRecordingStatus;
  const uploadStatus = (recordingObservable?.uploadStatus ??
    AudioRecordingUploadStatus.INITIAL) as AudioRecordingUploadStatus;
  const processingStatus = (recordingObservable?.processingStatus ??
    AudioRecordingProcessingStatus.INITIAL) as AudioRecordingProcessingStatus;
  const isPaused = audioStore.recording.isPaused;
  const isRecording = recordingStatus === AudioRecordingRecordingStatus.IN_PROGRESS && !isPaused;
  const canUpload =
    blobs.length > 0 &&
    recordingStatus === AudioRecordingRecordingStatus.COMPLETED &&
    uploadStatus !== AudioRecordingUploadStatus.IN_PROGRESS &&
    uploadStatus !== AudioRecordingUploadStatus.COMPLETED;
  const canTransform =
    uploadStatus === AudioRecordingUploadStatus.COMPLETED &&
    processingStatus !== AudioRecordingProcessingStatus.IN_PROGRESS &&
    processingStatus !== AudioRecordingProcessingStatus.COMPLETED;
  const canPlayAudio = blobs.length > 0 && !isRecording;

  const handleTriggerUpload = () => {
    if (!canUpload) return;
    setLoadError(null);
    void audioStore.startUploading({ audioRecordingId: recordingId });
  };

  const handleTriggerTransform = () => {
    if (!canTransform) return;
    setLoadError(null);
    void audioStore.startTransforming({ audioRecordingId: recordingId });
  };

  const handleRefresh = () => {
    if (isRefreshing) return;
    void loadRecordingData({ showSpinner: false });
  };

  const handleTrash = async () => {
    const confirmed = window.confirm(TRASH_CONFIRM_MESSAGE);
    if (!confirmed) return;

    setLoadError(null);
    try {
      await audioStore.trashRecording({ audioRecordingId: recordingId });
      await loadRecordingData();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to clear local data.";
      setLoadError(message);
    }
  };

  const handleViewActiveState = () => {
    store.navigation.goToAudioRecordingActiveState({
      openInSameTab: true,
      audioRecordingId: recordingId,
    });
  };

  const showContent = !isLoading && !loadError;

  return (
    <PageStack>
      <Card>
        <InfoHeader isLoading={isLoading} stats={headerStats} />

        <DurationRow>
          <MDSText variant={MDSTextVariant.HeaderS}>
            {headerStats.totalDurationLabel ?? "00:00"}
          </MDSText>
          <MDSText variant={MDSTextVariant.TextSm} color={MDSTextColor.Secondary}>
            total captured duration
          </MDSText>
        </DurationRow>

        <ActionRow>
          <MDSButton variant={MDSButtonVariant.Emphasis} onClick={handleViewActiveState}>
            Open active state page
          </MDSButton>
          <MDSButton
            variant={MDSButtonVariant.Transparent}
            onClick={handleRefresh}
            isDisabled={isRefreshing}
          >
            {isRefreshing ? "Refreshing…" : "Refresh local data"}
          </MDSButton>
        </ActionRow>

        {loadError && <InfoStatusMessage tone="error">{loadError}</InfoStatusMessage>}
        {isLoading && !loadError && <InfoStatusMessage>Loading local data…</InfoStatusMessage>}
      </Card>

      <AudioRecordingSyncStatusCard recording={recordingObservable} />

      <Card>
        <InfoRow>
          <MDSText variant={MDSTextVariant.TextSm} color={MDSTextColor.Secondary}>
            Audio recording ID
          </MDSText>
          <MDSText variant={MDSTextVariant.TextSm}>{recordingId}</MDSText>
        </InfoRow>
      </Card>

      <UploadProcessingCard
        canUpload={canUpload}
        canTransform={canTransform}
        onUpload={handleTriggerUpload}
        onTransform={handleTriggerTransform}
      />

      {showContent && (
        <SectionsStack>
          <Card>
            <InfoTranscriptionSection recording={recordingObservable} />
          </Card>
          <CapturedAudioBlobsCard blobs={blobs} />
        </SectionsStack>
      )}

      <CapturedAudioUtilitiesCard
        blobs={blobs}
        canPlay={canPlayAudio}
        onTriggerUpload={handleTriggerUpload}
        onTriggerTransform={handleTriggerTransform}
        canUpload={canUpload}
        canTransform={canTransform}
        uploadStatus={uploadStatus}
        processingStatus={processingStatus}
        onDeleteSession={handleTrash}
        deleteButtonLabel="Clear Local Data"
      />
    </PageStack>
  );
});

const PageStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding-top: 1rem;
  padding-left: 1.5rem;
  padding-right: 1.5rem;
  padding-bottom: 1.5rem;
`;

const SectionsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Card = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  border-radius: 0.75rem;
  background-color: ${({ theme }) => theme.colors.surface.pane};
`;

const InfoRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const DurationRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
`;

const ActionRow = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 0.75rem;
`;
